import { PLAN_LIST_Q_MAX } from '@developerz/domain';
import { createMemo, type JSX } from 'solid-js';
import { t } from '../i18n';
import { Field } from './Field';
import { Select } from './Select';

// The "Custom plans" section's toolbar (#4341): repo filter plus free-text
// search, both server-side (the request itself carries them; `?repo=` and
// `?q=` mirror the wire). Status filtering lives in the tab rail in
// `<CustomPlansSection/>`, not here — this component is presenter-only: two
// controls, both `<Field/>`, the section owns the repo vocabulary (from the
// response's `repos`) and the debounced search.
//
// Every control is `<Field/>`: `styles/field-usage.test.ts` fences the
// `field`/`field__label` scaffold to that one component, so a hand-rolled
// `<label>` here would be the file this guard exists to catch next.

export type CustomPlansFiltersProps = {
  readonly repo: string | null;
  readonly q: string;
  /** Every repo at least one plan in the response touches, sorted. */
  readonly repoOptions: readonly string[];
  readonly onRepo: (value: string | null) => void;
  readonly onQuery: (value: string) => void;
  /**
   * Forwarded to the search `<input>` so the section's own `/` shortcut can
   * focus it without a second document listener — see `CustomPlansSection.tsx`.
   */
  readonly searchRef?: (el: HTMLInputElement) => void;
};

export function CustomPlansFilters(props: CustomPlansFiltersProps): JSX.Element {
  // The currently-selected repo, even when it is not (yet) among `repoOptions` —
  // a `?repo=` deep link resolves before the FIRST response lands, when
  // `repoOptions` is still `[]`. Without a row for it the picker's trigger falls
  // back to the raw value and the open list checks nothing, so the filter reads
  // as unset. Once the response lands the chosen repo is normally already IN the
  // list, so this only ever adds the one entry a real answer omits.
  const options = createMemo((): readonly string[] => {
    const repo = props.repo;
    if (repo === null || props.repoOptions.includes(repo)) return props.repoOptions;
    return [repo, ...props.repoOptions];
  });

  return (
    <div class="custom-plans__filters">
      <Field label={t('fleet.plans.custom.search_label')} id="custom-plans-search">
        {(a11y) => (
          <input
            {...a11y}
            ref={props.searchRef}
            type="search"
            class="input"
            placeholder={t('fleet.plans.custom.search_placeholder')}
            maxLength={PLAN_LIST_Q_MAX}
            value={props.q}
            onInput={(event) => props.onQuery(event.currentTarget.value)}
          />
        )}
      </Field>

      <Field label={t('fleet.plans.custom.repo_label')} id="custom-plans-repo">
        {(a11y) => (
          <Select
            a11y={a11y}
            label={t('fleet.plans.custom.repo_label')}
            value={props.repo ?? ''}
            options={[
              { value: '', label: t('fleet.plans.custom.repo_all') },
              ...options().map((repo) => ({ value: repo, label: repo })),
            ]}
            onChange={(value) => props.onRepo(value === '' ? null : value)}
          />
        )}
      </Field>
    </div>
  );
}
